import { useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type WheelEvent } from 'react';
import styles from './FlowCanvas.module.css';

export interface CanvasNode {
  id: string;
  type: string;
  next: Record<string, string>;
  pos?: { x: number; y: number } | undefined;
}
export interface CanvasGraph {
  start: string;
  nodes: Record<string, CanvasNode>;
}
export interface CanvasType {
  label: string;
  category: string;
  portLabels: Record<string, string>;
}

const W = 210;
const HEAD = 34;
const ROW = 26;

const CATEGORY_COLOR: Record<string, string> = {
  Anruf: 'var(--text-secondary)',
  Ansage: '#6aa9d4',
  Eingabe: 'var(--accent)',
  Logik: '#b58ad6',
  Weiterleiten: 'var(--status-ok)',
  Daten: '#d4b86a',
};

const heightOf = (ports: number) => HEAD + Math.max(1, ports) * ROW + 8;

/** Einfache Spaltenanordnung von links nach rechts, ausgehend vom Startbaustein. */
export function autoLayout(graph: CanvasGraph): Record<string, { x: number; y: number }> {
  const depth = new Map<string, number>();
  const queue = [graph.start];
  depth.set(graph.start, 0);
  while (queue.length) {
    const id = queue.shift()!;
    for (const t of Object.values(graph.nodes[id]?.next ?? {})) {
      if (!depth.has(t) && graph.nodes[t]) {
        depth.set(t, depth.get(id)! + 1);
        queue.push(t);
      }
    }
  }
  let extra = Math.max(0, ...depth.values()) + 1;
  for (const id of Object.keys(graph.nodes)) if (!depth.has(id)) depth.set(id, extra++ % 1000);
  const rows = new Map<number, number>();
  const out: Record<string, { x: number; y: number }> = {};
  for (const id of Object.keys(graph.nodes)) {
    const d = depth.get(id)!;
    const row = rows.get(d) ?? 0;
    rows.set(d, row + 1);
    out[id] = { x: d * (W + 90), y: row * 190 };
  }
  return out;
}

interface Props {
  graph: CanvasGraph;
  types: Map<string, CanvasType>;
  ports: Record<string, string[]>;
  selected: string | null;
  onSelect: (id: string | null) => void;
  onMove: (id: string, pos: { x: number; y: number }) => void;
  onConnect: (id: string, port: string, target: string | null) => void;
  /** Rechtsklick: auf einen Baustein, eine Verbindung oder die freie Fläche. */
  onContext: (info: { kind: 'node'; id: string; x: number; y: number } | { kind: 'edge'; id: string; port: string; x: number; y: number } | { kind: 'canvas'; world: { x: number; y: number }; x: number; y: number }) => void;
  /** Doppelklick auf einen Baustein. */
  onOpen: (id: string) => void;
}

/** Zeichenfläche: Bausteine verschieben, Ausgänge per Ziehen verbinden, mit dem Mausrad zoomen, Fläche ziehen zum Verschieben. */
export function FlowCanvas({ graph, types, ports, selected, onSelect, onMove, onConnect, onContext, onOpen }: Props) {
  const [view, setView] = useState({ x: 40, y: 40, k: 1 });
  const [drag, setDrag] = useState<null | { kind: 'node'; id: string; dx: number; dy: number } | { kind: 'pan'; sx: number; sy: number; vx: number; vy: number } | { kind: 'link'; id: string; port: string; x: number; y: number }>(null);
  const svg = useRef<SVGSVGElement>(null);
  const lastClick = useRef<{ id: string; at: number }>({ id: '', at: 0 });
  const [hoverEdge, setHoverEdge] = useState<string | null>(null);

  const pos = (id: string) => graph.nodes[id]?.pos ?? { x: 0, y: 0 };
  const portsOf = (id: string) => ports[id] ?? ['default'];

  const toWorld = (clientX: number, clientY: number) => {
    const r = svg.current!.getBoundingClientRect();
    return { x: (clientX - r.left - view.x) / view.k, y: (clientY - r.top - view.y) / view.k };
  };

  const portY = (id: string, port: string) => pos(id).y + HEAD + Math.max(0, portsOf(id).indexOf(port)) * ROW + ROW / 2;

  const edges = useMemo(() => {
    const list: { from: string; port: string; to: string }[] = [];
    for (const n of Object.values(graph.nodes)) for (const [port, to] of Object.entries(n.next)) if (graph.nodes[to]) list.push({ from: n.id, port, to });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph]);

  const curve = (x1: number, y1: number, x2: number, y2: number) => {
    const c = Math.max(50, Math.abs(x2 - x1) / 2);
    return `M ${x1} ${y1} C ${x1 + c} ${y1}, ${x2 - c} ${y2}, ${x2} ${y2}`;
  };

  const down = (e: ReactPointerEvent<SVGSVGElement>) => {
    const el = e.target as Element;
    const portEl = el.closest('[data-port]');
    const nodeEl = el.closest('[data-node]');
    if (e.button !== 0) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    if (portEl) {
      const id = portEl.getAttribute('data-node-id')!;
      const port = portEl.getAttribute('data-port')!;
      const w = toWorld(e.clientX, e.clientY);
      setDrag({ kind: 'link', id, port, x: w.x, y: w.y });
    } else if (nodeEl) {
      const id = nodeEl.getAttribute('data-node')!;
      const w = toWorld(e.clientX, e.clientY);
      if (e.button !== 0) return;
      const now = Date.now();
      if (lastClick.current.id === id && now - lastClick.current.at < 380) {
        lastClick.current = { id: '', at: 0 };
        onOpen(id);
        return;
      }
      lastClick.current = { id, at: now };
      onSelect(id);
      setDrag({ kind: 'node', id, dx: w.x - pos(id).x, dy: w.y - pos(id).y });
    } else {
      onSelect(null);
      setDrag({ kind: 'pan', sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y });
    }
  };

  const move = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!drag) return;
    if (drag.kind === 'pan') setView((v) => ({ ...v, x: drag.vx + e.clientX - drag.sx, y: drag.vy + e.clientY - drag.sy }));
    else if (drag.kind === 'node') {
      const w = toWorld(e.clientX, e.clientY);
      onMove(drag.id, { x: Math.round(w.x - drag.dx), y: Math.round(w.y - drag.dy) });
    } else {
      const w = toWorld(e.clientX, e.clientY);
      setDrag({ ...drag, x: w.x, y: w.y });
    }
  };

  const up = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (drag?.kind === 'link') {
      const under = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-node]');
      const target = under?.getAttribute('data-node');
      if (target && target !== drag.id) onConnect(drag.id, drag.port, target);
    }
    setDrag(null);
  };

  const context = (e: ReactMouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    const el = e.target as Element;
    const edgeEl = el.closest('[data-edge]');
    const nodeEl = el.closest('[data-node]');
    if (edgeEl) onContext({ kind: 'edge', id: edgeEl.getAttribute('data-edge')!, port: edgeEl.getAttribute('data-edge-port')!, x: e.clientX, y: e.clientY });
    else if (nodeEl) {
      onSelect(nodeEl.getAttribute('data-node'));
      onContext({ kind: 'node', id: nodeEl.getAttribute('data-node')!, x: e.clientX, y: e.clientY });
    } else onContext({ kind: 'canvas', world: toWorld(e.clientX, e.clientY), x: e.clientX, y: e.clientY });
  };

  const wheel = (e: WheelEvent<SVGSVGElement>) => {
    const r = svg.current!.getBoundingClientRect();
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    setView((v) => {
      const k = Math.min(1.8, Math.max(0.4, v.k * (e.deltaY < 0 ? 1.1 : 0.9)));
      return { k, x: mx - ((mx - v.x) / v.k) * k, y: my - ((my - v.y) / v.k) * k };
    });
  };

  return (
    <div className={styles.wrap}>
      <svg ref={svg} className={styles.svg} onPointerDown={down} onPointerMove={move} onPointerUp={up} onWheel={wheel} onContextMenu={context} role="application" aria-label="Ablauf-Zeichenfläche">
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" className={styles.arrow} />
          </marker>
        </defs>
        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
          {edges.map((e) => {
            const a = pos(e.from);
            const b = pos(e.to);
            const d = curve(a.x + W, portY(e.from, e.port), b.x, b.y + HEAD / 2);
            const key = `${e.from}:${e.port}`;
            return (
              <g key={key}>
                <path d={d} className={`${styles.edge} ${hoverEdge === key ? styles.hot : ''}`} markerEnd="url(#arrow)" />
                <path d={d} className={styles.hit} data-edge={e.from} data-edge-port={e.port} onPointerEnter={() => setHoverEdge(key)} onPointerLeave={() => setHoverEdge(null)} />
              </g>
            );
          })}
          {drag?.kind === 'link' && <path d={curve(pos(drag.id).x + W, portY(drag.id, drag.port), drag.x, drag.y)} className={`${styles.edge} ${styles.draft}`} />}
          {Object.values(graph.nodes).map((n) => {
            const p = pos(n.id);
            const t = types.get(n.type);
            const pl = portsOf(n.id);
            const h = heightOf(pl.length);
            return (
              <g key={n.id} data-node={n.id} transform={`translate(${p.x} ${p.y})`} className={`${styles.node} ${selected === n.id ? styles.selected : ''}`}>
                <rect width={W} height={h} className={styles.box} />
                <rect width={4} height={h} fill={CATEGORY_COLOR[t?.category ?? ''] ?? 'var(--text-muted)'} />
                <text x={16} y={21} className={styles.title}>
                  {(t?.label ?? n.type).slice(0, 24)}
                </text>
                {n.id === graph.start && (
                  <text x={W - 10} y={21} textAnchor="end" className={styles.badge}>
                    START
                  </text>
                )}
                {pl.map((port, i) => {
                  const y = HEAD + i * ROW + ROW / 2;
                  const label = t?.portLabels[port] ?? (port === 'default' ? 'danach' : port);
                  const linked = Boolean(n.next[port]);
                  return (
                    <g key={port}>
                      <text x={16} y={y + 4} className={styles.port}>
                        {label.slice(0, 22)}
                      </text>
                      <circle cx={W} cy={y} r={7} data-port={port} data-node-id={n.id} className={`${styles.dot} ${linked ? styles.linked : ''}`}>
                        <title>Ziehen, um zu verbinden</title>
                      </circle>
                    </g>
                  );
                })}
                <circle cx={0} cy={HEAD / 2} r={5} className={styles.inlet} />
                <text x={0} y={-6} className={styles.id}>
                  {n.id}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
      <div className={styles.zoom}>
        <button type="button" onClick={() => setView((v) => ({ ...v, k: Math.min(1.8, v.k * 1.2) }))} aria-label="Vergrößern">
          +
        </button>
        <button type="button" onClick={() => setView((v) => ({ ...v, k: Math.max(0.4, v.k / 1.2) }))} aria-label="Verkleinern">
          −
        </button>
        <button type="button" onClick={() => setView({ x: 40, y: 40, k: 1 })} aria-label="Ansicht zurücksetzen">
          ⌂
        </button>
      </div>
    </div>
  );
}
